import type { TaskNode } from "../shared/types";

// Expanded/collapsed state is a per-user *view* preference, not shared plan data — so it lives in
// localStorage (per plan) and toggling it never touches the server. Keyed by the plan's guid in
// server mode, or "local" for the URL-hash plan. Only touches localStorage inside functions (never
// at import), so the node-env unit tests can import the pure helpers.

const key = (planKey: string) => "mpp:collapse:" + (planKey || "local");

// collect the ids of every collapsed node in the tree (pre-order)
export function collectCollapsed(tree: TaskNode[]): string[] {
  const out: string[] = [];
  (function walk(list: TaskNode[]) {
    for (const n of list) { if (n.collapsed) out.push(n.id); walk(n.children); }
  })(tree);
  return out;
}

// hydrate node.collapsed from the stored set. No stored prefs (never toggled here) → returns false
// and leaves the plan's own collapse state untouched, which then seeds localStorage on first render.
export function applyStoredCollapse(planKey: string, tree: TaskNode[]): boolean {
  let raw: string | null;
  try { raw = localStorage.getItem(key(planKey)); } catch { return false; }
  if (raw == null) return false;
  let ids: string[] = [];
  try { const p = JSON.parse(raw); if (Array.isArray(p)) ids = p.filter((x) => typeof x === "string"); } catch { return false; }
  const set = new Set(ids);
  (function walk(list: TaskNode[]) {
    for (const n of list) { if (n.children.length) n.collapsed = set.has(n.id); walk(n.children); }
  })(tree);
  return true;
}

// drop a plan's stored collapse state (e.g. when it's removed from the recents list)
export function clearCollapse(planKey: string): void {
  try { localStorage.removeItem(key(planKey)); } catch { /* ignore */ }
}

// persist the tree's current collapse state (called each render; skips redundant writes)
let lastKey = "", lastJson = "";
export function persistCollapse(planKey: string, tree: TaskNode[]): void {
  const k = key(planKey), json = JSON.stringify(collectCollapsed(tree));
  if (k === lastKey && json === lastJson) return; // unchanged → no write
  lastKey = k; lastJson = json;
  try { localStorage.setItem(k, json); } catch { /* quota / private mode: ignore */ }
}
